/**
 * Dashboard grid schema.
 *
 * Defines the grid layout, items, and component union used by `DashboardGrid`.
 * Plugins (e.g. the chat plugin) compose these primitives into their own
 * LLM-facing schemas — this module stays free of plugin-specific concerns.
 *
 * Grid validation is applied via Zod transform to ensure valid output:
 * - Auto-expands grid when items exceed bounds
 * - Shifts overlapping items to available positions
 * - Logs warnings when corrections are applied
 */
import { z } from 'zod';

import { PositionType } from '#core/schema/defs.ts';

import { ChartSchema } from './panels/chart/schema.ts';
import { KpiSchema } from './panels/kpi/schema.ts';
import { MarkdownSchema } from './panels/markdown/schema.ts';
import { TableSchema } from './panels/table/schema.ts';
import { logGridCorrections, validateAndCorrectGrid } from './validation.ts';

/**
 * Grid layout dimensions schema.
 */
export const GridLayoutSchema = z.object({
  rows: z.number().describe('Total number of grid rows'),
  columns: z.number().describe('Total number of grid columns'),
});

export type GridLayout = z.infer<typeof GridLayoutSchema>;

/**
 * Union of all component configurations.
 * Imports schemas from base schema files (single source of truth).
 * Component type is determined by the `type` field.
 */
export const ComponentSchema = z.union([ChartSchema, TableSchema, KpiSchema, MarkdownSchema]);

export type ComponentConfig = z.infer<typeof ComponentSchema>;
/** @public */
export type ComponentInput = z.input<typeof ComponentSchema>;

/** Component type literal union: 'chart' | 'table' | 'kpi' | 'markdown' */
export type ComponentType = ComponentConfig['type'];

/**
 * Grid item schema combining shared properties with component configuration.
 *
 * Position is optional at input to support streaming/partial data.
 * The DashboardGridSchema transform guarantees all items have complete positions via validateAndCorrectGrid.
 */
export const GridItemSchema = z.object({
  position: PositionType.optional().describe('Grid position and span (defaults to row 1, column 1 if omitted)'),
  component: ComponentSchema.describe('Component configuration with type discriminator'),
  /** Caption text displayed alongside the content */
  caption: z.string().optional().describe('Concise, short caption (2-4 words)'),
  /** Caption position - left for vertical sidebar, top for horizontal header */
  captionPosition: z.enum(['left', 'top']).optional().describe('Caption position'),
  /** Enable layer styling with gradient background and backdrop blur */
  layer: z.boolean().optional().describe('Enable layer styling with gradient background'),
});

/** @public */
export type GridItemInput = z.input<typeof GridItemSchema>;

/** GridItem output type with guaranteed position (after DashboardGridSchema transform). */
export type GridItem = Omit<z.output<typeof GridItemSchema>, 'position'> & {
  position: z.output<typeof PositionType>;
};

/**
 * Base dashboard grid layout schema (without validation transform).
 * Used internally for schema composition.
 */
const BaseDashboardGridSchema = z.object({
  layout: GridLayoutSchema,
  items: z.array(GridItemSchema).describe('Grid items to render'),
  title: z.string().optional().describe('Optional dashboard title'),
});

/**
 * Dashboard grid layout schema for multi-component dashboards.
 *
 * Includes validation transform that:
 * - Auto-expands grid when items exceed bounds
 * - Shifts overlapping items to available positions
 * - Logs warnings when corrections are applied
 */
export const DashboardGridSchema = BaseDashboardGridSchema.transform((data) => {
  // return data;
  const result = validateAndCorrectGrid(data.items, data.layout);
  if (result.corrections.length > 0) {
    logGridCorrections(result.corrections);
  }
  return {
    ...data,
    layout: result.layout,
    items: result.items,
  };
});

/** @public */
export type DashboardGridDataInput = z.input<typeof DashboardGridSchema>;

/** DashboardGridData output type with guaranteed positions on all items (after transform). */
export type DashboardGridData = Omit<z.output<typeof DashboardGridSchema>, 'items'> & {
  items: GridItem[];
};
